/**
 * Per-day NO-CRED overlay: rooms Symphony could not staff because NOBODY WORKING holds the credentials for them.
 *
 * Braden 2026-09-18: "if you don't have an unstaffable room maybe put a box on that says no cred for no credentialed."
 * That distinction matters operationally — NEED MD sends the runner hunting for a free provider, while NO CRED tells
 * them the room was never fillable and the fix is credentialing (or the roster), not another phone call.
 *
 * Symphony reports it per room in `unstaffedRooms[].reason`; the board stores it per day like the float, relief and
 * OB-hold overlays so it survives a refresh, and rewrites it on each allocation. Never leaves the machine.
 */
export const noCredKey = (date: string) => `oc-nocred-${date}`;

/** operatingRoomId -> which slot(s) nobody is credentialed for. */
export type NoCredMap = Record<string, { doctor?: boolean; crna?: boolean }>;

export function loadNoCred(date: string): NoCredMap {
  try {
    const raw = localStorage.getItem(noCredKey(date));
    if (raw) {
      const v = JSON.parse(raw);
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const out: NoCredMap = {};
        for (const k of Object.keys(v)) {
          const e = v[k];
          if (e && typeof e === 'object' && (e.doctor || e.crna)) out[k] = { doctor: !!e.doctor, crna: !!e.crna };
        }
        return out;
      }
    }
  } catch { /* ignore */ }
  return {};
}

/** Replace the day's overlay from Symphony's own unstaffed list. A fresh allocation supersedes the previous one. */
export function applyNoCred(
  date: string,
  unstaffed: { operatingRoomId: string; missing: 'crna' | 'anesthesiologist' | 'both'; reason: string }[] | undefined,
): NoCredMap {
  const map: NoCredMap = {};
  for (const u of unstaffed || []) {
    // The reason string is the contract: only a "no credentialed ..." (or "nobody credentialed") reason means the
    // room was unfillable. A plain "no anesthesiologist available" is an ordinary shortage and stays NEED MD.
    if (!/credentialed/i.test(u.reason || '')) continue;
    const entry = map[u.operatingRoomId] || {};
    if (u.missing === 'anesthesiologist' || u.missing === 'both') entry.doctor = true;
    if (u.missing === 'crna' || u.missing === 'both') entry.crna = true;
    map[u.operatingRoomId] = entry;
  }
  try {
    if (Object.keys(map).length) localStorage.setItem(noCredKey(date), JSON.stringify(map));
    else localStorage.removeItem(noCredKey(date));
  } catch { /* ignore */ }
  return map;
}

/** Drop the day's overlay (a re-run that staffs everything must not leave stale NO CRED boxes behind). */
export function clearNoCred(date: string): NoCredMap {
  try { localStorage.removeItem(noCredKey(date)); } catch { /* ignore */ }
  return {};
}
